/**
 * Authentication hook using Zustand
 */
import { create } from 'zustand';
import { authApi } from '@/lib/api';
import type { UserInfo, LoginRequest } from '@/types';

// 2026-07-19 (Item 8 / C-TOOL-DEV, Fable finding): DEV_MODE auth-
// bypass constant removed. One character (false → true) away from
// disabling admin login in prod — the exact class of change nobody
// notices in a busy diff. For UI testing without a real backend, use
// a mock-service-worker layer instead of an in-app bypass.

interface AuthState {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  login: async (credentials: LoginRequest) => {
    try {
      set({ isLoading: true, error: null });

      // Login and get token
      const { data: tokenData } = await authApi.login(credentials);

      // Store token
      localStorage.setItem('access_token', tokenData.access_token);

      // Get user info
      const { data: userData } = await authApi.getCurrentUser();

      set({
        user: userData,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || 'Login failed. Please try again.';
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: errorMessage,
      });
      throw error;
    }
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // Ignore logout errors
      console.error('Logout error:', error);
    } finally {
      // Clear state and token regardless of API success
      localStorage.removeItem('access_token');
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },

  checkAuth: async () => {
    const token = localStorage.getItem('access_token');

    if (!token) {
      set({ isAuthenticated: false, isLoading: false });
      return;
    }

    try {
      set({ isLoading: true });

      // Verify token and get user info
      const { data } = await authApi.getCurrentUser();

      set({
        user: data,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      // Token is invalid, clear it
      localStorage.removeItem('access_token');
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },
}));
